import {
    Autocomplete,
    AutocompleteRenderInputParams,
    CircularProgress,
    IconButton,
    TextField,
    Tooltip
} from '@mui/material';
import React, {
    FocusEvent,
    MouseEvent,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import type { CreativeCredit } from 'sr-types/lib/shortlist/v1/graphql';
import { useGetCreativeCreditsAndExperiences } from '../../../../components/person/person';
import { SortableChips } from '../../../../components/SortableChipsComponents/SortableChips';
import { ChipOption } from '../../../../components/SortableChipsComponents/types';
import I18n, { useI18n } from '../../../../i18n/I18n';
import { Icons } from '../../../../icons/Icons';
import { coerceKeyCreditsList } from '../../../data/utils';
import type { KeyCreditOption, KeyCreditsLineItemRow } from '../../../types';
import {
    buildFreeTextAddKeyCreditOption,
    buildKeyCreditOptions,
    buildSelectedKeyCreditOptions,
    canAddFreeTextKeyCreditOption,
    filterKeyCreditOptions,
    formatKeyCredit,
    getKeyCreditsPersonId,
    hasVisibleKeyCreditOptions,
    isFocusWithinAutocomplete,
    keyCreditsFormDropdownPopperModifiers
} from '../../utils';
import AddKeyCreditModal from '../AddKeyCreditModal';
import KeyCreditsDropdownOption from '../dataDisplay/KeyCreditsDropdownOption';
import KeyCreditsAutocompletePaper, { KeyCreditsAutocompletePaperProps } from '../internal/KeyCreditsAutocompletePaper';

export type KeyCreditsFormFieldProps = {
    value: CreativeCredit[] | null | undefined;
    onChange: (next: CreativeCredit[]) => void;
    lineItemRow?: KeyCreditsLineItemRow;
    label?: ReactNode;
    disabled?: boolean;
    autoFocus?: boolean;
    openDropdownOnInput?: boolean;
    allowFreeTextAdd?: boolean;
    isAIEnabled?: boolean;
    onOpenKeyCreditsAI?: () => void;
};

export default function KeyCreditsFormField({
    value,
    onChange,
    lineItemRow,
    label,
    disabled = false,
    autoFocus = false,
    openDropdownOnInput = false,
    allowFreeTextAdd = false,
    isAIEnabled = false,
    onOpenKeyCreditsAI
}: KeyCreditsFormFieldProps) {
    const t = useI18n();
    const [isAddKeyCreditModalOpen, setIsAddKeyCreditModalOpen] = useState(false);
    const [addModalProductionName, setAddModalProductionName] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [listOpen, setListOpen] = useState(false);
    const [shouldLoadPersonCredits, setShouldLoadPersonCredits] = useState(false);
    const skipNextInputBlurRef = useRef(false);
    const personId = getKeyCreditsPersonId(lineItemRow?.itemReference);
    const prevPersonIdRef = useRef(personId);

    const { creditsAndExperienceDetails, creditsAndExperienceDetailsLoading } = useGetCreativeCreditsAndExperiences(
        personId,
        !personId || !shouldLoadPersonCredits
    );

    const handleAddFreeTextCredit = useCallback((textValue: string) => {
        setAddModalProductionName(textValue);
        setIsAddKeyCreditModalOpen(true);
    }, []);

    const handleCloseAddKeyCreditModal = useCallback(() => {
        setIsAddKeyCreditModalOpen(false);
        setAddModalProductionName('');
    }, []);

    const handleAddKeyCredit = useCallback(
        (credit: CreativeCredit) => {
            const newCredit = {
                ...credit,
                keyCredit: true
            } as CreativeCredit;
            onChange([...coerceKeyCreditsList(value), newCredit]);
        },
        [onChange, value]
    );

    const selectedCredits: CreativeCredit[] = useMemo(() => coerceKeyCreditsList(value), [value]);

    const personCredits = useMemo(
        () => creditsAndExperienceDetails?.person?.creativeCredits ?? [],
        [creditsAndExperienceDetails?.person?.creativeCredits]
    );

    const selectedValue: KeyCreditOption[] = useMemo(
        () => buildSelectedKeyCreditOptions(selectedCredits, personCredits),
        [selectedCredits, personCredits]
    );

    const options: KeyCreditOption[] = useMemo(() => buildKeyCreditOptions(personCredits), [personCredits]);

    const showPersonCreditsLoading = creditsAndExperienceDetailsLoading && options.length === 0;
    const hasDropdownOptions = options.length > 0;

    useLayoutEffect(() => {
        const prevPersonId = prevPersonIdRef.current ?? '';
        const nextPersonId = personId ?? '';

        if (prevPersonId !== nextPersonId) {
            if (coerceKeyCreditsList(value).length > 0) {
                onChange([]);
                setInputValue('');
                setListOpen(false);
            }
            setShouldLoadPersonCredits(false);
            prevPersonIdRef.current = personId;
        }
    }, [personId, onChange, value]);

    const showAddCreditsCta = isAIEnabled && Boolean(onOpenKeyCreditsAI);

    const canShowFreeTextAdd = useMemo(
        () => allowFreeTextAdd && canAddFreeTextKeyCreditOption(options, selectedValue, inputValue),
        [allowFreeTextAdd, inputValue, options, selectedValue]
    );

    const canShowDropdown = hasVisibleKeyCreditOptions(options, inputValue, selectedValue);
    const canOpenDropdownPanel = canShowDropdown || canShowFreeTextAdd || showAddCreditsCta;
    const showAiAdornment = showAddCreditsCta && selectedCredits.length === 0;

    useEffect(() => {
        if (!personId) {
            setListOpen(false);
            setShouldLoadPersonCredits(false);
        }
    }, [personId]);

    const mergedFilterOptions = useCallback(
        (opts: KeyCreditOption[], params: Parameters<typeof filterKeyCreditOptions>[1]) => {
            const filtered = filterKeyCreditOptions(opts, params);
            const term = params.inputValue.trim();
            if (allowFreeTextAdd && canAddFreeTextKeyCreditOption(opts, selectedValue, term)) {
                filtered.push(
                    buildFreeTextAddKeyCreditOption(term, t('shortlist.line.item.key.credits.add', { value: term }))
                );
            }
            return filtered;
        },
        [allowFreeTextAdd, selectedValue, t]
    );

    const handleChange = useCallback(
        (_event: unknown, next: KeyCreditOption[]) => {
            const freeTextOpt = next.find((opt) => opt?.isFreeTextAdd);
            if (freeTextOpt) {
                skipNextInputBlurRef.current = true;
                handleAddFreeTextCredit(freeTextOpt.credit.showName?.trim() || inputValue.trim());
                setInputValue('');
                setListOpen(false);
                return;
            }

            const dedup: CreativeCredit[] = [];
            const seen = new Set<string>();
            for (const opt of next) {
                if (!opt || seen.has(opt.key)) continue;
                seen.add(opt.key);
                dedup.push(opt.credit);
            }
            onChange(dedup);
        },
        [handleAddFreeTextCredit, inputValue, onChange]
    );

    const handleReorder = useCallback(
        (chips: ChipOption[]) => {
            const keyToCredit = new Map(selectedValue.map((opt) => [opt.key, opt.credit]));
            const next = chips
                .map((chip) => keyToCredit.get(chip.id ?? ''))
                .filter((c): c is CreativeCredit => Boolean(c));
            if (next.length === selectedValue.length) {
                onChange(next);
            }
        },
        [selectedValue, onChange]
    );

    const handleInputBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            if (isAddKeyCreditModalOpen) return;

            const related = event.relatedTarget as Element | null;
            if (isFocusWithinAutocomplete(related)) return;

            window.setTimeout(() => {
                if (skipNextInputBlurRef.current) {
                    skipNextInputBlurRef.current = false;
                    setListOpen(false);
                    return;
                }
                if (isFocusWithinAutocomplete(document.activeElement)) return;
                setListOpen(false);
            }, 0);
        },
        [isAddKeyCreditModalOpen]
    );

    const handleOpenAiPanel = useCallback(
        (e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            setListOpen(false);
            onOpenKeyCreditsAI?.();
        },
        [onOpenKeyCreditsAI]
    );

    return (
        <>
            {/* Tooltip shows when the field is disabled */}
            <Tooltip title={disabled ? t('shortlist.line.item.key.credits.disabled.tooltip') : undefined}>
                <Autocomplete<KeyCreditOption, true, true, false>
                    multiple
                    autoFocus={autoFocus}
                    inputValue={inputValue}
                    onInputChange={(_, nextInputValue) => {
                        setInputValue(nextInputValue);
                        if (personId && !shouldLoadPersonCredits) {
                            setShouldLoadPersonCredits(true);
                        }
                        if (openDropdownOnInput && nextInputValue.trim()) {
                            setListOpen(true);
                        }
                    }}
                    open={listOpen && canOpenDropdownPanel}
                    onOpen={() => {
                        if (personId && !shouldLoadPersonCredits) {
                            setShouldLoadPersonCredits(true);
                        }
                        setListOpen(true);
                    }}
                    onClose={() => setListOpen(false)}
                    openOnFocus={hasDropdownOptions || canShowFreeTextAdd || showAddCreditsCta}
                    disableCloseOnSelect
                    disableClearable
                    filterSelectedOptions
                    size="small"
                    fullWidth
                    disabled={disabled}
                    loading={false}
                    options={options}
                    value={selectedValue}
                    onChange={handleChange}
                    getOptionLabel={(opt) => opt.label}
                    isOptionEqualToValue={(opt, val) => opt.key === val.key}
                    filterOptions={allowFreeTextAdd ? mergedFilterOptions : filterKeyCreditOptions}
                    renderOption={(optionProps, option) => (
                        <li {...optionProps} key={option.key}>
                            <KeyCreditsDropdownOption option={option} />
                        </li>
                    )}
                    renderTags={(tagValue, getTagProps) => {
                        const chips: ChipOption[] = tagValue.map((opt) => ({
                            id: opt.key,
                            label: formatKeyCredit(opt.credit) || opt.label
                        }));
                        return (
                            <SortableChips
                                chips={chips}
                                getTagProps={getTagProps}
                                fixedOptions={[]}
                                onReorder={handleReorder}
                                getChipKey={(chip) => chip.id ?? chip.label}
                            />
                        );
                    }}
                    slots={{
                        paper: showAddCreditsCta ? KeyCreditsAutocompletePaper : undefined
                    }}
                    slotProps={{
                        popper: {
                            placement: 'bottom-start',
                            modifiers: keyCreditsFormDropdownPopperModifiers
                        },
                        paper: showAddCreditsCta
                            ? ({
                                  showAddCreditsCta,
                                  emptyListArea: showAddCreditsCta && !canShowDropdown && !canShowFreeTextAdd,
                                  onOpenAiPanel: handleOpenAiPanel,
                                  sx: {
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'stretch',
                                      width: '100%'
                                  }
                              } as KeyCreditsAutocompletePaperProps)
                            : undefined,
                        listbox: {
                            sx:
                                showAddCreditsCta && !canShowDropdown && !canShowFreeTextAdd
                                    ? { display: 'none' }
                                    : undefined
                        }
                    }}
                    noOptionsText={null}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField
                            {...params}
                            autoFocus={autoFocus}
                            variant="outlined"
                            label={label}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    onBlur: (event: FocusEvent<HTMLInputElement>) => {
                                        handleInputBlur(event);
                                    },
                                    endAdornment: (
                                        <>
                                            {showPersonCreditsLoading ? (
                                                <CircularProgress
                                                    color="inherit"
                                                    size={14}
                                                    sx={{ flexShrink: 0, mr: 0.5 }}
                                                />
                                            ) : null}
                                            {showAiAdornment ? (
                                                <Tooltip title={<I18n token="shortlist.key.credits.ai.tooltip" />}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={t('shortlist.key.credits.ai.extract.credits')}
                                                        onClick={handleOpenAiPanel}
                                                        sx={{ p: 0, flexShrink: 0 }}
                                                    >
                                                        <Icons.AutoAwesomeGradient sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}

                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }
                            }}
                        />
                    )}
                />
            </Tooltip>
            {allowFreeTextAdd && isAddKeyCreditModalOpen ? (
                <AddKeyCreditModal
                    isOpen={isAddKeyCreditModalOpen}
                    initialProductionName={addModalProductionName}
                    onClose={handleCloseAddKeyCreditModal}
                    onAdd={handleAddKeyCredit}
                    lineItemRow={lineItemRow}
                />
            ) : null}
        </>
    );
}
